<script setup lang="ts">
import { reactive, ref, watch, computed, provide, inject } from 'vue'
import { useRouter } from 'vue-router'
import countries from '@/components/FormSection/countries.json'
import {
  validator,
  triggerFieldValidationForUrlParams,
} from '@/components/FormSection/validator'
import FormLabel from '@/components/FormSection/FormLabel.vue'
import FormFieldGroup from '@/components/FormSection/FormFieldGroup.vue'
import FormRequiredSymbol from '@/components/FormSection/FormRequiredSymbol.vue'
import FormFieldError from '@/components/FormSection/FormFieldError.vue'
import AddressSection from '@/components/FormSection/AddressSection.vue'
import AuthorisationSection from '@/components/FormSection/AuthorisationSection.vue'
import { sendDetails } from '@/components/FormSection/sendDetails'
import { resetExternalError } from '@/components/FormSection/resetExternalError'
import { emptyQueryObj } from '@/components/FormSection/urlParamsHelperDonorDetails'
import type { DonorDetails, UrlParamsDonorDetails } from '@/types'
import { DEFAULT_COUNTRY, GIBRALTAR_POSTCODE } from '@/types'
import formatPostcode from '@/components/FormSection/formatPostcode'

const router = useRouter()

const urlParamsDonorDetails = inject<UrlParamsDonorDetails<DonorDetails>>(
  'urlParamsDonorDetails',
  emptyQueryObj
)

const isReauthMode = inject<boolean>('reauthMode', false)

const hasEmailSignature = inject<boolean>('hasEmailSignature', false)

const updateEmailLinkUrl = import.meta.env.VITE_SA_UPDATE_EMAIL_URL || ''

const countriesList = countries
const hasSelectedAddress = ref(false)
const postingDonorDetails = ref(false)
const formState = reactive({
  ...urlParamsDonorDetails,
  country: countries.includes(urlParamsDonorDetails.country || '')
    ? urlParamsDonorDetails.country
    : DEFAULT_COUNTRY,
  agree: false,
})

const { v$, $externalResults } = validator(formState)

const emit = defineEmits(['submit'])

provide('formState', {
  formState,
  v$,
  hasSelectedAddress,
  DEFAULT_COUNTRY,
  $externalResults,
})

const hasExternalError = computed(() => {
  const er = $externalResults.value
  const isEmpty = Object.keys(er).length === 0
  const hasErrors = !Object.values(er).every((p: any) => p === undefined)

  return !isEmpty && hasErrors
})

const uniqueErrors = computed(() => {
  return [...new Set(v$.value.$errors.map((error) => error.$message))]
})

triggerFieldValidationForUrlParams(v$, urlParamsDonorDetails)

watch(v$.value.$errors, () => {
  if (v$.value.$errors.length === 0) {
    v$.value.$clearExternalResults()
  }
})

watch(
  () => formState.country,
  (country) => {
    if (country === 'Gibraltar') {
      formState.postcode = formatPostcode(GIBRALTAR_POSTCODE)
    }
  }
)

const erroredPropertyList = computed(() => {
  return v$.value.$errors.map((error: any) => {
    let property = error.$property

    if (property === 'postcode' && formState.country !== 'United Kingdom') {
      property = 'zipcode'
    } else if (
      property === 'zipcode' &&
      formState.country === 'United Kingdom'
    ) {
      property = 'postcode'
    }

    return property
  })
})

const focusField = (fieldName: string) => {
  const el = document.querySelector(
    `#${fieldName}, [name='${fieldName}']`
  ) as HTMLElement | null
  if (el) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
    el.focus()
  }
}

const submitAttempted = ref(false)

const handleSendDetails = async () => {
  submitAttempted.value = true
  if (v$.value.$invalid) {
    v$.value.$validate()
    setTimeout(() => {
      focusField(erroredPropertyList.value[0])
    }, 0)
    return
  }
  await sendDetails(
    formState,
    v$,
    $externalResults,
    postingDonorDetails,
    emit,
    router
  )
}

const handleResetExternalError = (fieldName: string) => {
  resetExternalError(fieldName, v$, $externalResults)
}

const submitButtonText = isReauthMode
  ? 'reauthForm.formFields.submit.text'
  : 'form.formFields.submit.text'
</script>

<template>
  <div class="pb-3 ps-3 small text-center text-muted fst-italic">
    Fields marked with
    <form-required-symbol />
    are mandatory.
  </div>
  <form data-testid="signup-form">
    <form-field-group class="w-lg-75 mx-auto" data-testid="email-field-group">
      <form-label for="emailAddress">
        {{ $t('form.formFields.emailAddress.label') }}
        <form-required-symbol />
      </form-label>
      <input
        v-model="v$.emailAddress.$model"
        type="email"
        id="emailAddress"
        name="emailAddress"
        autocomplete="email"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.emailAddress.$error && v$.emailAddress.$dirty,
          'is-invalid': v$.emailAddress.$error && v$.emailAddress.$dirty,
        }"
        :placeholder="$t('form.formFields.emailAddress.placeholder')"
        :readonly="hasEmailSignature"
        data-testid="email-input"
        @blur="handleResetExternalError('emailAddress')"
      />

      <div>
        <div
          v-if="isReauthMode && hasEmailSignature"
          class="ps-3 text-start small"
        >
          <a :href="updateEmailLinkUrl">
            {{ $t('form.formFields.emailAddress.updateEmailLinkText') }}
          </a>
        </div>
        <form-field-error
          v-if="v$.emailAddress.$error"
          :errors="v$.emailAddress.$errors"
        ></form-field-error>
      </div>
    </form-field-group>

    <form-field-group class="w-lg-75 mx-auto">
      <form-label for="firstname">
        {{ $t('form.formFields.firstname.label') }}
        <form-required-symbol />
      </form-label>
      <input
        v-model="v$.firstname.$model"
        id="firstname"
        name="firstname"
        autocomplete="given-name"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.firstname.$error && v$.firstname.$dirty,
          'is-invalid': v$.firstname.$error && v$.firstname.$dirty,
        }"
        :placeholder="$t('form.formFields.firstname.placeholder')"
        data-testid="firstname-input"
        @blur="handleResetExternalError('firstname')"
      />
      <form-field-error
        v-if="v$.firstname.$error"
        :errors="v$.firstname.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group class="w-lg-75 mx-auto">
      <form-label for="lastname">
        {{ $t('form.formFields.lastname.label') }}
        <form-required-symbol />
      </form-label>
      <input
        v-model="v$.lastname.$model"
        id="lastname"
        name="lastname"
        autocomplete="family-name"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.lastname.$error && v$.lastname.$dirty,
          'is-invalid': v$.lastname.$error && v$.lastname.$dirty,
        }"
        :placeholder="$t('form.formFields.lastname.placeholder')"
        data-testid="lastname-input"
        @blur="handleResetExternalError('lastname')"
      />
      <form-field-error
        v-if="v$.lastname.$error"
        :errors="v$.lastname.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group class="w-lg-75 mx-auto">
      <form-label for="country">
        {{ $t('form.formFields.country.label') }}
        <form-required-symbol />
      </form-label>
      <select
        v-model="v$.country.$model"
        name="country"
        class="form-select form-select-lg is-valid"
        :class="{
          'is-valid': !v$.country.$error && v$.country.$dirty,
          'is-invalid': v$.country.$error && v$.country.$dirty,
        }"
        data-testid="country-select"
        @change="hasSelectedAddress = true"
      >
        <option value="" disabled>
          {{ $t('form.formFields.country.placeholder') }}
        </option>
        <option
          v-for="country in countriesList"
          :key="country"
          :value="country"
        >
          {{ country }}
        </option>
      </select>
    </form-field-group>

    <address-section />

    <suspense>
      <authorisation-section />
    </suspense>

    <div
      v-if="hasExternalError"
      class="mt-2 mb-4 text-warning"
      data-testid="external-error-section"
    >
      An error occurred sending your details please try again or contact us if
      the problem persists

      <div v-for="error in uniqueErrors" :key="error.toString()">
        <li>{{ error }}</li>
      </div>
    </div>

    <div>
      <button
        class="btn btn-lg w-100 w-lg-75"
        data-testid="submit-button"
        :class="{
          'btn-primary': !submitAttempted || (!v$.$dirty && !v$.$invalid),
          'btn-warning disabled pe-auto': submitAttempted && v$.$invalid,
          'btn-success': v$.$dirty && !v$.$invalid,
        }"
        @click.prevent="handleSendDetails"
      >
        <div v-if="postingDonorDetails" role="status" aria-hidden="true">
          <span class="spinner-border spinner-border-sm"></span>
          <span class="small ms-3">Sending...</span>
        </div>
        <span v-else>
          {{ $t(submitButtonText) }}
        </span>
      </button>
      <div v-if="submitAttempted && v$.$invalid && erroredPropertyList.length">
        <div class="mt-2 mb-4 text-warning fw-bold">
          Please fix the error{{ erroredPropertyList.length > 1 ? 's' : '' }} in
          the form before continuing.
        </div>
      </div>
    </div>

    <div>
      <div class="mt-3 small text-center text-muted opacity-75 text-balance">
        This site is protected by reCAPTCHA and the Google
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>
        and
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Service
        </a>
        apply.
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@media (min-width: 992px) {
  .w-lg-75 {
    max-width: 75%;
  }
}
</style>
